import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/site";
import { LogoLockup } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled ? "border-b border-brass/20 bg-navy/90 shadow-xl backdrop-blur-xl" : "bg-gradient-to-b from-navy/70 to-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
                <Link to="/" aria-label="Vistaaryatra home">
                    <LogoLockup />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex">
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                            className={`link-underline text-sm font-medium tracking-wide transition-colors duration-200 ${
                                pathname === link.to ? "text-brass" : "text-ivory/85 hover:text-brass"
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={CONTACT.phoneHref}
                        data-testid="navbar-phone-link"
                        className="hidden items-center gap-2 text-sm text-ivory/85 transition-colors hover:text-brass md:flex"
                    >
                        <Phone className="h-4 w-4 text-brass" />
                        {CONTACT.phoneDisplay}
                    </a>
                    <Link
                        to="/book"
                        data-testid="navbar-book-cta"
                        className="hidden rounded-full bg-brass px-6 py-2.5 text-sm font-semibold text-navy transition-all duration-300 hover:bg-brass-dark hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.7)] sm:block"
                    >
                        Book Now
                    </Link>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="navbar-mobile-menu"
                                aria-label="Open menu"
                                className="rounded-md border border-ivory/20 p-2 text-ivory lg:hidden"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72 border-brass/20 bg-navy p-0">
                            <div className="flex flex-col gap-1 p-6 pt-16">
                                {NAV_LINKS.map((link) => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                                        className={`rounded-md px-3 py-3 font-serif text-xl transition-colors ${
                                            pathname === link.to ? "text-brass" : "text-ivory hover:bg-white/5"
                                        }`}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                                <Link
                                    to="/book"
                                    data-testid="mobile-book-cta"
                                    className="mt-4 rounded-full bg-brass px-6 py-3 text-center text-sm font-semibold text-navy"
                                >
                                    Book Now
                                </Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};
